<template>
  <div class="space-y-6">
    <h1 class="text-3xl font-bold text-neutral-900 dark:text-neutral-100">HomeBase Containers</h1>

    <!-- Create Container Form -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft p-6">
      <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6">Create New Container</h2>
      <div class="flex flex-wrap items-center gap-4">
        <input
          v-model="newContainer.serial"
          type="text"
          placeholder="Serial Number"
          aria-label="Serial number"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        />

        <input
          v-model="newContainer.capacityL"
          type="number"
          placeholder="Capacity (L)"
          aria-label="Capacity in litres"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        />

        <select
          v-model="newContainer.type"
          aria-label="Container type"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option value="wheelieBin">Wheelie Bin</option>
          <option value="barrel">Barrel</option>
          <option value="tank">Tank</option>
        </select>

        <button
          @click="createContainer"
          :disabled="!newContainer.serial || !newContainer.capacityL || creating"
          class="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg v-if="creating" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ creating ? 'Creating…' : 'Create Container' }}
        </button>
      </div>
    </div>

    <!-- Containers List -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft p-6">
      <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6">Container Inventory</h2>
      <div class="flex flex-wrap items-center gap-4 mb-6">
        <select
          v-model="filters.unassigned"
          aria-label="Filter by assignment"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option value="">All Containers</option>
          <option value="true">Unassigned Only</option>
          <option value="false">Assigned Only</option>
        </select>

        <select
          v-model="filters.limit"
          aria-label="Maximum results to load"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option :value="100">100 containers</option>
          <option :value="500">500 containers</option>
          <option :value="1000">1000 containers</option>
          <option :value="5000">5000 containers</option>
        </select>

        <button
          @click="reload"
          class="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          Apply Filters
        </button>
      </div>

      <AlertBanner
        v-if="error && containers.length"
        type="error"
        message="Couldn't refresh containers — showing the last loaded list."
        :dismissible="false"
        class="mb-4"
      />

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="flex flex-col items-center gap-3">
          <svg class="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-sm text-neutral-600 dark:text-neutral-400">Loading containers...</p>
        </div>
      </div>

      <!-- Error State (nothing loaded yet) -->
      <ErrorState
        v-else-if="error && !containers.length"
        title="Couldn't load containers"
        :message="error"
        @retry="reload"
      />

      <!-- Empty State -->
      <div v-else-if="!sortedAndFiltered.length" class="py-12 text-center">
        <div class="flex flex-col items-center gap-3">
          <span class="material-icons text-5xl text-neutral-400 dark:text-neutral-600" aria-hidden="true">inbox</span>
          <p class="text-neutral-600 dark:text-neutral-400">No containers found</p>
        </div>
      </div>

      <!-- Data Table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
            <tr>
              <th :aria-sort="ariaSort('serial')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('serial')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Serial
                  <span v-if="sortState.key === 'serial'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('type')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('type')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Type
                  <span v-if="sortState.key === 'type'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('capacityL')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('capacityL')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Capacity
                  <span v-if="sortState.key === 'capacityL'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('assignedHousehold')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('assignedHousehold')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Assigned To
                  <span v-if="sortState.key === 'assignedHousehold'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Status</th>
              <th :aria-sort="ariaSort('createdAt')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('createdAt')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Created
                  <span v-if="sortState.key === 'createdAt'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <tr v-for="container in sortedAndFiltered" :key="container.id" class="hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors">
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ container.serial }}</td>
              <td class="px-4 py-3 text-sm">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                  :class="{
                    'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300': container.type === 'wheelieBin',
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300': container.type === 'barrel',
                    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300': container.type === 'tank',
                    'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200': !['wheelieBin', 'barrel', 'tank'].includes(container.type)
                  }"
                >
                  {{ humanize(container.type) }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm tabular-nums text-neutral-700 dark:text-neutral-300">{{ container.capacityL }}L</td>
              <td class="px-4 py-3 text-sm">
                <span
                  v-if="container.assignedHouseholdVilla || container.assignedHouseholdId || container.currentHouseholdId || container.assignedHousehold"
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                >
                  <template v-if="container.assignedHouseholdVilla">
                    {{ container.assignedHouseholdVilla }}<span v-if="container.assignedHouseholdCommunity"> - {{ container.assignedHouseholdCommunity }}</span>
                  </template>
                  <template v-else>
                    {{ container.assignedHouseholdId || container.currentHouseholdId || container.assignedHousehold }}
                  </template>
                </span>
                <span
                  v-else
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200"
                >
                  Unassigned
                </span>
              </td>
              <td class="px-4 py-3 text-sm">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                  :class="{
                    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': container.assignedHouseholdId || container.currentHouseholdId || container.assignedHousehold,
                    'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200': !container.assignedHouseholdId && !container.currentHouseholdId && !container.assignedHousehold
                  }"
                >
                  {{ getContainerStatus(container) }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ (container.createdAt || '').slice(0,10) }}</td>
              <td class="px-4 py-3 text-sm">
                <button
                  @click="viewDetails(container)"
                  class="text-sm px-3 py-1.5 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  View Details
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center gap-4 mt-6">
        <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
          Showing: {{ containers.length }} containers
        </span>
        <span v-if="containers.length >= filters.limit" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
          Limit reached - increase limit to see more
        </span>
      </div>
    </div>

    <!-- Container Detail Modal -->
    <Modal
      :show="showDetailModal"
      :title="`Container Details - ${selectedContainer?.serial || ''}`"
      size="xl"
      :close-on-backdrop="true"
      @close="closeDetailModal"
    >
      <div v-if="detailLoading" class="flex items-center justify-center py-12">
        <div class="flex flex-col items-center gap-3">
          <svg class="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-sm text-neutral-600 dark:text-neutral-400">Loading container details...</p>
        </div>
      </div>

      <ErrorState
        v-else-if="detailError"
        title="Couldn't load container details"
        :message="detailError"
        @retry="loadDetails"
      />

      <div v-else-if="containerDetail" class="space-y-6">
        <!-- Container Info -->
        <div class="bg-neutral-50 dark:bg-neutral-900 rounded-lg p-4">
          <h4 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Container Information</h4>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-neutral-600 dark:text-neutral-400">Serial</p>
              <p class="text-base font-medium text-neutral-900 dark:text-neutral-100">{{ containerDetail.serial }}</p>
            </div>
            <div>
              <p class="text-sm text-neutral-600 dark:text-neutral-400">Type</p>
              <p class="text-base font-medium text-neutral-900 dark:text-neutral-100">{{ humanize(containerDetail.type) }}</p>
            </div>
            <div>
              <p class="text-sm text-neutral-600 dark:text-neutral-400">Capacity</p>
              <p class="text-base font-medium tabular-nums text-neutral-900 dark:text-neutral-100">{{ containerDetail.capacityL }}L</p>
            </div>
            <div>
              <p class="text-sm text-neutral-600 dark:text-neutral-400">Status</p>
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                :class="{
                  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': containerDetail.assignedHouseholdId || containerDetail.currentHouseholdId || containerDetail.assignedHousehold,
                  'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200': !containerDetail.assignedHouseholdId && !containerDetail.currentHouseholdId && !containerDetail.assignedHousehold
                }"
              >
                {{ getContainerStatus(containerDetail) }}
              </span>
            </div>
            <div>
              <p class="text-sm text-neutral-600 dark:text-neutral-400">Assigned To</p>
              <p class="text-base font-medium text-neutral-900 dark:text-neutral-100">{{ containerDetail.assignedHouseholdId || containerDetail.currentHouseholdId || containerDetail.assignedHousehold || 'Unassigned' }}</p>
            </div>
            <div>
              <p class="text-sm text-neutral-600 dark:text-neutral-400">Created</p>
              <p class="text-base font-medium text-neutral-900 dark:text-neutral-100">{{ formatDateTime(containerDetail.createdAt) }}</p>
            </div>
          </div>
        </div>

        <!-- Container History -->
        <div v-if="containerHistory" class="space-y-6">
          <!-- Assignments -->
          <div v-if="containerHistory.assignments?.length" class="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
            <h4 class="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Assignments</h4>
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                  <tr>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Household</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Assigned At</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Status</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
                  <tr v-for="assignment in containerHistory.assignments" :key="assignment.id" class="hover:bg-neutral-50 dark:hover:bg-neutral-800">
                    <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ assignment.householdId }}</td>
                    <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ formatDateTime(assignment.assignedAt) }}</td>
                    <td class="px-4 py-3 text-sm">
                      <span
                        class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                        :class="{
                          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': assignment.active,
                          'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200': !assignment.active
                        }"
                      >
                        {{ assignment.active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Deployments -->
          <div v-if="containerHistory.deployments?.length" class="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
            <h4 class="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Deployments</h4>
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                  <tr>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Type</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Household</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Performed At</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Performed By</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
                  <tr v-for="deployment in containerHistory.deployments" :key="deployment.id" class="hover:bg-neutral-50 dark:hover:bg-neutral-800">
                    <td class="px-4 py-3 text-sm">
                      <span
                        class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                        :class="{
                          'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300': deployment.type === 'deployment',
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300': deployment.type === 'swap',
                          'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200': deployment.type !== 'deployment' && deployment.type !== 'swap'
                        }"
                      >
                        {{ humanize(deployment.type) }}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ deployment.householdId }}</td>
                    <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ formatDateTime(deployment.performedAt) }}</td>
                    <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ deployment.performedBy }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Collections -->
          <div v-if="containerHistory.collections?.length" class="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
            <h4 class="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Collections</h4>
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                  <tr>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Date</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Volume</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Weight</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Collected By</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
                  <tr v-for="collection in containerHistory.collections" :key="collection.requestId || collection.id" class="hover:bg-neutral-50 dark:hover:bg-neutral-800">
                    <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ formatDateTime(collection.completedAt || collection.requestedAt || collection.collectedAt) }}</td>
                    <td class="px-4 py-3 text-sm tabular-nums text-neutral-700 dark:text-neutral-300">{{ collection.metrics?.volumeL || collection.volumeL || '-' }}L</td>
                    <td class="px-4 py-3 text-sm tabular-nums text-neutral-700 dark:text-neutral-300">{{ collection.metrics?.weightKg || collection.weightKg || '-' }}kg</td>
                    <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ collection.performedBy || collection.collectedBy || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="!containerHistory.assignments?.length && !containerHistory.deployments?.length && !containerHistory.collections?.length" class="text-center py-12">
            <p class="text-neutral-500 dark:text-neutral-400">No history available for this container.</p>
          </div>
        </div>
      </div>

      <template #footer>
        <button
          @click="closeDetailModal"
          class="px-4 py-2 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          Close
        </button>
      </template>
    </Modal>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue'
import useHomebaseContainers from '@/composables/useHomebaseContainers'
import useToast from '@/composables/useToast'
import Modal from '@/components/ui/Modal.vue'
import ErrorState from '@/components/ui/ErrorState.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'

export default {
  name: 'HomeBaseContainers',
  components: { Modal, ErrorState, AlertBanner },
  setup() {
    const {
      containers,
      containerDetail,
      loading,
      detailLoading,
      error,
      params,
      fetchContainers,
      fetchContainerDetail,
      createContainer: createContainerAPI,
      fetchContainerHistory
    } = useHomebaseContainers()

    const { showToast } = useToast()

    const filters = reactive({
      unassigned: '',
      limit: 1000
    })

    const newContainer = reactive({
      serial: '',
      capacityL: '',
      type: 'wheelieBin'
    })

    const showDetailModal = ref(false)
    const selectedContainer = ref(null)
    const containerHistory = ref(null)
    const detailError = ref(null)

    const applyFilters = () => {
      params.value.unassigned = filters.unassigned
      params.value.limit = filters.limit
      fetchContainers()
    }

    const reload = () => {
      applyFilters()
    }

    const sortState = reactive({ key: 'createdAt', dir: 'desc' })
    const sortBy = (key) => {
      if (sortState.key === key) {
        sortState.dir = sortState.dir === 'asc' ? 'desc' : 'asc'
      } else {
        sortState.key = key
        sortState.dir = 'asc'
      }
    }

    const ariaSort = (key) =>
      sortState.key === key ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : 'none'

    const sortedAndFiltered = computed(() => {
      return [...containers.value].sort((a, b) => {
        const va = (a[sortState.key] || '')
        const vb = (b[sortState.key] || '')
        if (va < vb) return sortState.dir === 'asc' ? -1 : 1
        if (va > vb) return sortState.dir === 'asc' ? 1 : -1
        return 0
      })
    })

    const formatDateTime = (value) => (value ? value.slice(0, 16).replace('T', ' ') : '-')

    // 'wheelieBin' -> 'Wheelie Bin', 'deployment_task' -> 'Deployment Task'
    const humanize = (value) => {
      if (!value) return value
      return String(value)
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    }

    const creating = ref(false)
    const createContainer = async () => {
      if (!newContainer.serial || !newContainer.capacityL || creating.value) return

      creating.value = true
      try {
        await createContainerAPI({
          serial: newContainer.serial,
          capacityL: parseInt(newContainer.capacityL),
          type: newContainer.type
        })
        newContainer.serial = ''
        newContainer.capacityL = ''
        newContainer.type = 'wheelieBin'
        showToast('success', 'Container created')
      } catch (err) {
        showToast('error', 'Failed to create container. Please try again.')
      } finally {
        creating.value = false
      }
    }

    const loadDetails = async () => {
      if (!selectedContainer.value) return
      detailError.value = null
      // The composable writes detail failures into the page-level error ref;
      // snapshot it so a modal failure doesn't trigger the page error banner.
      const pageError = error.value
      try {
        await fetchContainerDetail(selectedContainer.value.id)
        containerHistory.value = await fetchContainerHistory(selectedContainer.value.id)
      } catch (err) {
        detailError.value = err.detail || err.message || 'Failed to load container details'
        error.value = pageError
      }
    }

    const viewDetails = (container) => {
      selectedContainer.value = container
      containerHistory.value = null
      showDetailModal.value = true
      loadDetails()
    }

    const closeDetailModal = () => {
      showDetailModal.value = false
      selectedContainer.value = null
      containerDetail.value = null
      containerHistory.value = null
      detailError.value = null
    }

    const getContainerStatus = (container) => {
      if (container.assignedHouseholdId || container.currentHouseholdId || container.assignedHousehold) return 'Deployed'
      return 'Unassigned'
    }

    onMounted(() => {
      fetchContainers()
    })

    return {
      containers,
      containerDetail,
      loading,
      detailLoading,
      error,
      filters,
      newContainer,
      showDetailModal,
      selectedContainer,
      containerHistory,
      detailError,
      reload,
      sortedAndFiltered,
      sortBy,
      sortState,
      ariaSort,
      formatDateTime,
      humanize,
      creating,
      createContainer,
      viewDetails,
      loadDetails,
      closeDetailModal,
      getContainerStatus
    }
  }
}
</script>
